import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import { RouterEnum } from '@/router/router.enums';
import { setAuditScreen } from '@/utils/clientHeaders';
import { message } from 'ant-design-vue';
import {
  getAccessToken,
  getSupportSession,
  isSupportEnded,
  isSupportTab,
} from '@/utils/tokenStore';
import {
  blockedPathInSupportTab,
  isSupportPagePath,
  supportEndedUrl,
} from '@/utils/supportSession';
import { MESSAGES } from '@/utils/standardErrors';
import { canOpen, homePath } from '@/utils/access';

const SupportEnter = () =>
  import(/* webpackChunkName: "SupportEnter" */ '../views/SupportEnter.vue');

const Signin = () =>
  import(/* webpackChunkName: "Signin" */ '../views/Signin/Signin.vue');
const SetIdAndPw = () =>
  import(/* webpackChunkName: "SetIdAndPw" */ '../views/Signin/SetIdAndPw.vue');
const Dashboard = () =>
  import(
    /* webpackChunkName: "Dashboard" */ '../views/Dashboard/Dashboard.vue'
  );
const CustomerManagement = () =>
  import(
    /* webpackChunkName: "CustomerManagement" */ '../views/CustomerManagement/CustomerManagement.vue'
  );
const CustomerDetail = () =>
  import(
    /* webpackChunkName: "CustomerDetail" */ '../views/CustomerDetail/CustomerDetail.vue'
  );

// 구독 관리
const SubscriptionProducts = () =>
  import(
    /* webpackChunkName: "SubscriptionProducts" */ '../views/Subscriptions/products/ProductList.vue'
  );
const SubscriptionPromotions = () =>
  import(
    /* webpackChunkName: "SubscriptionPromotions" */ '../views/Subscriptions/promotions/PromotionList.vue'
  );
const SubscriptionManagement = () =>
  import(
    /* webpackChunkName: "SubscriptionManagement" */ '../views/Subscriptions/SubscriptionManagement.vue'
  );
const UpsertSubscription = () =>
  import(
    /* webpackChunkName: "UpsertSubscription" */ '../views/Subscriptions/UpsertSubscription.vue'
  );
const SubscriptionDetail = () =>
  import(
    /* webpackChunkName: "SubscriptionDetail" */ '../views/Subscriptions/SubscriptionDetail.vue'
  );
const PaymentHistory = () =>
  import(
    /* webpackChunkName: "PaymentHistory" */ '../views/Subscriptions/PaymentHistory.vue'
  );

// Callbot usage fee ('콜봇 사용료')
const CallbotUsageFee = () =>
  import(
    /* webpackChunkName: "CallbotUsageFee" */ '../views/CallbotUsageFee/CallbotUsageFee.vue'
  );
const CallbotUsageFeeMonthList = () =>
  import(
    /* webpackChunkName: "CallbotUsageFee" */ '../views/CallbotUsageFee/tabs/MonthListTab.vue'
  );
const CallbotUsageFeeProfiles = () =>
  import(
    /* webpackChunkName: "CallbotUsageFee" */ '../views/CallbotUsageFee/tabs/ProfilesTab.vue'
  );
const CallbotUsageFeeOps = () =>
  import(
    /* webpackChunkName: "CallbotUsageFee" */ '../views/CallbotUsageFee/tabs/OpsTab.vue'
  );
const CallbotUsageStatementPrint = () =>
  import(
    /* webpackChunkName: "CallbotUsageStatementPrint" */ '../views/CallbotUsageFee/print/UsageStatementPrint.vue'
  );

// 직원 계정(리셀러 서브 계정·접근 권한)
const StaffManagement = () =>
  import(
    /* webpackChunkName: "StaffManagement" */ '../views/Staff/StaffManagement.vue'
  );

// 연동관리
const ChannelManagementList = () =>
  import(
    /* webpackChunkName: "ChannelManagementList" */ '../views/Integration/ChannelManagementList.vue'
  );

// 보안 설정
const ChangePassword = () =>
  import(
    /* webpackChunkName: "ChangePassword" */ '../views/Security/ChangePassword.vue'
  );

const MyAccountSecurity = () =>
  import(
    /* webpackChunkName: "MyAccountSecurity" */ '../views/Security/MyAccountSecurity.vue'
  );

// 2단계 인증
const TwoFactorSettings = () =>
  import(
    /* webpackChunkName: "TwoFactorSettings" */ '../views/TwoFactorAuth/TwoFactorSettings.vue'
  );
const TwoFactorVerify = () =>
  import(
    /* webpackChunkName: "TwoFactorVerify" */ '../views/TwoFactorAuth/TwoFactorVerify.vue'
  );

const routes: Array<RouteRecordRaw> = [
  // 지원 접속 진입·끝(로그인 검사 밖, 운영자가 리셀러 회사에 — 10-29)
  {
    name: RouterEnum.SupportEnter.name,
    path: RouterEnum.SupportEnter.path,
    component: SupportEnter,
  },
  {
    name: RouterEnum.SupportEnded.name,
    path: RouterEnum.SupportEnded.path,
    component: SupportEnter,
  },
  {
    path: '/',
    name: '/',
    redirect: () =>
      getAccessToken()
        ? RouterEnum.CustomerManagement.path
        : RouterEnum.Signin.path,
  },
  {
    name: RouterEnum.Signin.name,
    path: RouterEnum.Signin.path,
    component: Signin,
  },
  {
    name: RouterEnum.Signin.SetIdAndPw.name,
    path: RouterEnum.Signin.SetIdAndPw.path,
    component: SetIdAndPw,
  },
  {
    name: RouterEnum.Dashboard.name,
    path: RouterEnum.Dashboard.path,
    component: Dashboard,
  },
  {
    name: RouterEnum.CustomerManagement.name,
    path: RouterEnum.CustomerManagement.path,
    component: CustomerManagement,
  },
  {
    name: RouterEnum.CustomerDetail.name,
    path: RouterEnum.CustomerDetail.path,
    component: CustomerDetail,
  },
  // 구독 관리
  {
    name: RouterEnum.Subscription.Products.name,
    path: RouterEnum.Subscription.Products.path,
    component: SubscriptionProducts,
  },
  {
    name: RouterEnum.Subscription.Promotions.name,
    path: RouterEnum.Subscription.Promotions.path,
    component: SubscriptionPromotions,
  },
  // 구독관리 - 통합 (탭으로 서비스 구분)
  {
    name: RouterEnum.Subscription.name,
    path: RouterEnum.Subscription.path,
    redirect: RouterEnum.Subscription.Chatbot.path,
  },
  // 챗봇 구독
  {
    name: RouterEnum.Subscription.Chatbot.name,
    path: RouterEnum.Subscription.Chatbot.path,
    component: SubscriptionManagement,
  },
  {
    name: RouterEnum.Subscription.Chatbot.Create.name,
    path: RouterEnum.Subscription.Chatbot.Create.path,
    component: UpsertSubscription,
  },
  {
    name: RouterEnum.Subscription.Chatbot.Detail.name,
    path: RouterEnum.Subscription.Chatbot.Detail.path,
    component: SubscriptionDetail,
  },
  {
    name: RouterEnum.Subscription.Chatbot.Edit.name,
    path: RouterEnum.Subscription.Chatbot.Edit.path,
    component: UpsertSubscription,
  },
  // 음성봇 구독
  {
    name: RouterEnum.Subscription.Voicebot.name,
    path: RouterEnum.Subscription.Voicebot.path,
    component: SubscriptionManagement,
  },
  {
    name: RouterEnum.Subscription.Voicebot.Create.name,
    path: RouterEnum.Subscription.Voicebot.Create.path,
    component: UpsertSubscription,
  },
  {
    name: RouterEnum.Subscription.Voicebot.Detail.name,
    path: RouterEnum.Subscription.Voicebot.Detail.path,
    component: SubscriptionDetail,
  },
  {
    name: RouterEnum.Subscription.Voicebot.Edit.name,
    path: RouterEnum.Subscription.Voicebot.Edit.path,
    component: UpsertSubscription,
  },
  // 센터플로우 구독
  {
    name: RouterEnum.Subscription.Centerflow.name,
    path: RouterEnum.Subscription.Centerflow.path,
    component: SubscriptionManagement,
  },
  // 전체 결제내역
  {
    name: RouterEnum.Subscription.PaymentHistory.name,
    path: RouterEnum.Subscription.PaymentHistory.path,
    component: PaymentHistory,
  },
  {
    name: RouterEnum.Subscription.Centerflow.Create.name,
    path: RouterEnum.Subscription.Centerflow.Create.path,
    component: UpsertSubscription,
  },
  {
    name: RouterEnum.Subscription.Centerflow.Detail.name,
    path: RouterEnum.Subscription.Centerflow.Detail.path,
    component: SubscriptionDetail,
  },
  {
    name: RouterEnum.Subscription.Centerflow.Edit.name,
    path: RouterEnum.Subscription.Centerflow.Edit.path,
    component: UpsertSubscription,
  },
  // Callbot usage fee: the three tabs are child routes rendered in the page's
  // <router-view> (the first nested route of this router). Open to every
  // signed-in user like the other menus (no operator guard).
  {
    name: RouterEnum.CallbotUsageFee.name,
    path: RouterEnum.CallbotUsageFee.path,
    component: CallbotUsageFee,
    redirect: RouterEnum.CallbotUsageFee.Months.path,
    children: [
      {
        name: RouterEnum.CallbotUsageFee.Months.name,
        path: RouterEnum.CallbotUsageFee.Months.path,
        component: CallbotUsageFeeMonthList,
      },
      {
        name: RouterEnum.CallbotUsageFee.Profiles.name,
        path: RouterEnum.CallbotUsageFee.Profiles.path,
        component: CallbotUsageFeeProfiles,
      },
      {
        name: RouterEnum.CallbotUsageFee.Ops.name,
        path: RouterEnum.CallbotUsageFee.Ops.path,
        component: CallbotUsageFeeOps,
      },
    ],
  },
  // Usage statement print page: no sidenav (App.vue).
  {
    name: RouterEnum.CallbotUsageFee.UsageStatement.name,
    path: RouterEnum.CallbotUsageFee.UsageStatement.path,
    component: CallbotUsageStatementPrint,
  },
  {
    name: RouterEnum.Staff.name,
    path: RouterEnum.Staff.path,
    component: StaffManagement,
  },
  // 연동관리
  {
    name: RouterEnum.Integration.ChannelManagement.name,
    path: RouterEnum.Integration.ChannelManagement.path,
    component: ChannelManagementList,
  },
  // 보안 설정
  {
    name: RouterEnum.Security.ChangePassword.name,
    path: RouterEnum.Security.ChangePassword.path,
    component: ChangePassword,
  },
  {
    name: RouterEnum.Security.MyAccount.name,
    path: RouterEnum.Security.MyAccount.path,
    component: MyAccountSecurity,
  },
  // 2단계 인증
  {
    name: RouterEnum.TwoFactorAuth.Settings.name,
    path: RouterEnum.TwoFactorAuth.Settings.path,
    component: TwoFactorSettings,
  },
  {
    name: RouterEnum.TwoFactorAuth.Verify.name,
    path: RouterEnum.TwoFactorAuth.Verify.path,
    component: TwoFactorVerify,
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: () =>
      getAccessToken()
        ? RouterEnum.CustomerManagement.path
        : RouterEnum.Signin.path,
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  linkActiveClass: 'active',
});

const ACCESSTOKEN_NOTALLOWED_PATHS: string[] = [
  RouterEnum.Signin.path,
  RouterEnum.Signin.SetIdAndPw.path,
  RouterEnum.TwoFactorAuth.Verify.path,
];

router.beforeEach(async (to, from, next) => {
  if (isSupportPagePath(to.path)) return next();
  // 끝난 지원 탭은 끝 화면에만(일반 로그인 토큰으로 넘어가지 않게)
  if (isSupportEnded())
    return next(supportEndedUrl(getSupportSession()?.reason));
  const accessToken = getAccessToken();
  const isAllowedPath = ACCESSTOKEN_NOTALLOWED_PATHS.some((path) =>
    to.path.startsWith(path)
  );

  if (accessToken) {
    if (isAllowedPath) {
      return next(RouterEnum.CustomerManagement.path);
    } else if (blockedPathInSupportTab(to.path, isSupportTab())) {
      // 지원 탭에서 메뉴에 안 보이는 보안설정을 주소로 열면 고객 목록(늘 보임)으로 + 안내 한 번
      message.warning({ content: MESSAGES.ROUTE_DENIED, key: 'ROUTE_DENIED' });
      return next(RouterEnum.CustomerManagement.path);
    }
    // 내 권한·담당 고객사로 못 여는 메뉴(11-02 리셀러 직원) → 열 수 있는 첫 화면으로. 막기는 서버가 함
    // supportAccess → axios → router 순환을 피해 부를 때 불러옴
    const { loadSupportAccess, myAccess } = await import(
      '@/utils/supportAccess'
    );
    await loadSupportAccess();
    if (!canOpen(to.path, myAccess)) {
      const home = homePath(myAccess);
      if (to.path !== home) {
        message.warning({
          content: MESSAGES.ROUTE_DENIED,
          key: 'ROUTE_DENIED',
        });
        return next(home);
      }
    }
    return next();
  } else {
    if (isAllowedPath) {
      return next();
    } else {
      return next(RouterEnum.Signin.path);
    }
  }
});

router.afterEach((to) => {
  setAuditScreen(to);
  window.scrollTo({ top: 0, behavior: 'smooth' });
});

export default router;
